// Client-side Italian IBAN validation (mirrors backend algorithm exactly).
// Used for immediate UX feedback; the backend remains authoritative.
export function normalizeIBAN(raw) {
  if (!raw) return '';
  return String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function mod97(str) {
  // "Big-number-safe" iterative mod
  let mod = 0;
  for (const ch of str) {
    mod = (mod * 10 + Number(ch)) % 97;
  }
  return mod;
}

/**
 * Validate an Italian IBAN.
 * Returns { valid: boolean, normalized: string, messageKey: string }
 *   messageKey: 'iban_empty' | 'iban_invalid' | 'iban_valid'
 */
export function validateItalianIBAN(raw) {
  const iban = normalizeIBAN(raw);
  if (!iban) return { valid: false, normalized: '', messageKey: 'iban_empty' };
  if (!iban.startsWith('IT') || iban.length !== 27) {
    return { valid: false, normalized: iban, messageKey: 'iban_invalid' };
  }
  const checkDigits = iban.slice(2, 4);
  const cin = iban.slice(4, 5);
  const abi = iban.slice(5, 10);
  const cab = iban.slice(10, 15);
  const account = iban.slice(15, 27);
  if (!/^\d{2}$/.test(checkDigits)) return { valid: false, normalized: iban, messageKey: 'iban_invalid' };
  if (!/^[A-Z]$/.test(cin)) return { valid: false, normalized: iban, messageKey: 'iban_invalid' };
  if (!/^\d{5}$/.test(abi) || !/^\d{5}$/.test(cab)) return { valid: false, normalized: iban, messageKey: 'iban_invalid' };
  if (!/^[A-Z0-9]{12}$/.test(account)) return { valid: false, normalized: iban, messageKey: 'iban_invalid' };
  // MOD-97
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged.split('').map(ch => /[A-Z]/.test(ch) ? String(ch.charCodeAt(0) - 55) : ch).join('');
  if (mod97(numeric) !== 1) return { valid: false, normalized: iban, messageKey: 'iban_invalid' };
  return { valid: true, normalized: iban, messageKey: 'iban_valid' };
}

/** Return a bank-style masked IBAN: "IT60 •••• •••• •••• •••• 3456" (always 4 bullet groups) */
export function maskIBAN(iban) {
  const norm = normalizeIBAN(iban);
  if (!norm || norm.length < 8) return norm;
  const head = norm.slice(0, 4);
  const tail = norm.slice(-4);
  return `${head} •••• •••• •••• •••• ${tail}`;
}
